import { z } from 'zod';

const envSchema = z.object({
  VITE_SUPABASE_URL: z.string().url('VITE_SUPABASE_URL must be a valid URL'),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, 'VITE_SUPABASE_ANON_KEY is required'),
  VITE_USE_MOCKS: z
    .string()
    .optional()
    .transform((v) => v === 'true'),
  VITE_E2E_MODE: z
    .string()
    .optional()
    .transform((v) => v === 'true'),
  VITE_SENTRY_DSN: z.string().optional().default(''),
  // Accept any string (incl. '' or a relative '/'-prefixed path); .url() would reject
  // same-origin defaults. The effective base is resolved in resolveApiBaseUrl below.
  VITE_API_BASE_URL: z.string().optional(),
});

type RawEnv = z.infer<typeof envSchema>;
type Env = Omit<RawEnv, 'VITE_API_BASE_URL'> & { VITE_API_BASE_URL: string };

/**
 * Resolve the effective API base URL.
 *
 * Precedence: explicit env override > conditional default.
 *  - Explicit `VITE_API_BASE_URL` (non-empty) always wins.
 *  - Mock-mode → `http://localhost:3043` so browser MSW handlers (registered for that
 *    origin) keep matching without touching mock-handlers.
 *  - Otherwise → `''` (relative same-origin): `${base}/v1/...` resolves to `/v1/...`
 *    against the page origin → traefik routes `dev-admin.wandero.pl/v1/*` → :3043
 *    (locally the vite `/v1` proxy forwards to :3043).
 */
function resolveApiBaseUrl(explicit: string | undefined, useMocks: boolean): string {
  if (explicit !== undefined && explicit !== '') return explicit;
  if (useMocks) return 'http://localhost:3043';
  return '';
}

function parseEnv(): Env {
  const result = envSchema.safeParse(import.meta.env);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `  ${i.path.join('.')}: ${i.message}`)
      .join('\n');
    throw new Error(`Invalid environment variables:\n${issues}`);
  }
  const { VITE_API_BASE_URL: explicitBase, ...rest } = result.data;
  return {
    ...rest,
    VITE_API_BASE_URL: resolveApiBaseUrl(explicitBase, rest.VITE_USE_MOCKS),
  };
}

export const env = parseEnv();
